// M16 T21 — Özet sağ rail: sete bağlı projelerde form yanıtı ↔ KPI değeri uyuşmazlıkları
// (yalnız ikisi de dolu ve farklı). Tek tık senkron iki yön; sonra liste yenilenir.
// Finar 14.4 (K4): `unit_mismatch` satırı "bağ hatalı — birimler uyuşmuyor"; yazma düğmesi yok.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatKpiNumber, type KpiFormMismatch } from "@mahir/shared";
import { applyFormToKpi, applyKpiToForm, listKpiSetFormMismatches } from "../../api/kpi-checks";
import { KPI_CHECKS_COPY as C } from "../../lib/kpi-checks-copy";

interface Props {
  setId: string | null;
  canEdit: boolean;
  onChanged?: (() => void) | undefined;
}

export function KpiFormMismatchList({ setId, canEdit, onChanged }: Props): JSX.Element {
  const [rows, setRows] = useState<KpiFormMismatch[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!setId) {
      setRows([]);
      return;
    }
    try {
      setRows(await listKpiSetFormMismatches(setId));
    } catch {
      setRows([]);
    }
  }, [setId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const act = async (key: string, fn: () => Promise<unknown>): Promise<void> => {
    setBusy(key);
    try {
      await fn();
      await reload();
      onChanged?.();
    } finally {
      setBusy(null);
    }
  };

  return (
    <section
      aria-label={C.formTitle}
      className="rounded-xl p-3 text-sm"
      style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
    >
      <h3 className="font-semibold mb-2" style={{ color: "var(--color-text)" }}>
        {C.formTitle} {rows.length > 0 ? `(${rows.length})` : ""}
      </h3>
      {rows.length === 0 ? (
        <p className="text-xs" style={{ color: "var(--color-text-3)" }}>
          {C.formNone}
        </p>
      ) : (
        <ul className="flex flex-col gap-2 text-xs">
          {rows.map((m) => {
            const key = `${m.projectId}:${m.questionCode}`;
            const broken = m.kind === "unit_mismatch";
            return (
              <li
                key={key}
                className="rounded-lg p-2"
                style={{ background: "var(--color-warning-bg)", color: "var(--color-text)" }}
              >
                <div className="font-medium">{m.indicatorLabel}</div>
                <div style={{ color: "var(--color-text-2)" }}>
                  {m.questionLabel} · {m.year} — {C.formValue}:{" "}
                  <b>{m.formValue !== null ? formatKpiNumber(m.formValue) : "—"}</b>
                  {/* birimler farklıysa (çevrilebilir ya da hatalı bağ) form değerinin birimi ayrı */}
                  {m.questionUnit && m.questionUnit !== m.unit ? ` ${m.questionUnit}` : ""} ·{" "}
                  {C.kpiValue}: <b>{m.kpiValue !== null ? formatKpiNumber(m.kpiValue) : "—"}</b>
                  {m.unit ? ` ${m.unit}` : ""}
                </div>
                {broken ? (
                  <div
                    style={{ color: "var(--color-warning-text)" }}
                    data-testid="kpi-form-link-broken"
                  >
                    {C.unitMismatchRow(m.unit ?? C.unitless, m.questionUnit ?? C.unitless)}
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-2 mt-1">
                  <Link to={`/projects/${m.projectId}/form`} className="underline">
                    {C.openForm}
                  </Link>
                  {canEdit && !broken && m.formValue !== null && (
                    <button
                      type="button"
                      className="underline"
                      disabled={busy === key}
                      onClick={() =>
                        void act(key, () => applyFormToKpi(m.projectId, m.questionCode))
                      }
                    >
                      {C.writeToKpi}
                    </button>
                  )}
                  {canEdit &&
                    !broken &&
                    m.questionType !== "percent" &&
                    m.suggestedValue !== null && (
                      <button
                        type="button"
                        className="underline"
                        disabled={busy === key}
                        onClick={() =>
                          void act(key, () => applyKpiToForm(m.projectId, m.questionCode))
                        }
                      >
                        {C.writeToForm}
                      </button>
                    )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
